import { useEffect } from 'react';
import { withStyles } from 'vopay';
import { useParams, useHistory } from 'react-router-dom';
import { Title, Button, UtilButton } from 'UI';
import styles from './ClientAccountPage.module.scss';
import ClientAccountDetail from './ClientAccountDetail';
import { useClientAccountDetailData } from 'shared/contacts';
import { usePermissions } from '@/providers/Permissions.provider';
import { useTranslation } from 'react-i18next';
import { useNavMenu } from '@/providers/NavMenu.provider';

const cn = withStyles(styles);

type ClientAccountPageProps = {
  // Shows the new flow's embedded Receivables/Payables tabs. Only the orphan
  // /accounting/client-accounts/:accountID route sets it while the new
  // Accounting Software API is validated (ID-13335); the menu-linked page
  // stays as on develop.
  accountingEnabled?: boolean;
};

export default function ClientAccountPage({ accountingEnabled = false }: ClientAccountPageProps) {
  const history = useHistory();
  const { accountID } = useParams<{ accountID: string }>();
  const { permissions } = usePermissions();
  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccount' });
  const { t: tCustomer } = useTranslation('translations', { keyPrefix: 'Customers' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { setSubNavActiveModule } = useNavMenu();
  const detail = useClientAccountDetailData(accountID);
  const { clientAccount } = detail;

  useEffect(() => {
    setSubNavActiveModule('clientAccountsList');
  }, []);

  return (
    <div className={cn('clientAccountPage')}>
      <UtilButton
        iconLeft="chevron_left"
        size="large"
        onClick={() => {
          if (history.length > 1) {
            history.goBack();
          } else {
            history.replace('/client-accounts');
          }
        }}
      >
        {history.length > 1 ? tGlobal('Buttons.back') : t('clientAccounts', { defaultValue: 'Client Accounts' })}
      </UtilButton>

      {clientAccount && (
        <header className={cn('pageHeader')}>
          <Title>{t('pageTitle', { defaultValue: 'Client Account Details' })}</Title>

          <div className={cn('pageHeaderLinks')}>
            {permissions.CreateClientAccountTransfers && (
              <Button
                onClick={() => {
                  history.push('/client-account-transfer-funds');
                }}
                disabled={!clientAccount.IsActive}
              >
                {t('transferFunds', { defaultValue: 'Transfer Funds' })}
              </Button>
            )}

            {permissions.CreatePayments && (
              <Button
                onClick={() => {
                  history.push('/single-payment', {
                    contact: clientAccount.ClientAccountID,
                  });
                }}
                disabled={!clientAccount.IsActive}
              >
                {tCustomer('payment', {
                  defaultValue: 'Payment',
                })}
              </Button>
            )}

            {permissions.CreateCollections && (
              <Button
                onClick={() => {
                  history.push('/single-collection', {
                    contact: clientAccount.ClientAccountID,
                  });
                }}
                disabled={!clientAccount.IsActive}
              >
                {tCustomer('collection', {
                  defaultValue: 'Collection',
                })}
              </Button>
            )}
          </div>
        </header>
      )}

      <ClientAccountDetail accountID={accountID} detail={detail} accountingEnabled={accountingEnabled} />
    </div>
  );
}
